import { itemDef, type WeaponStats } from "./items.ts";

/**
 * Arco e flechas (doc 02 §12; doc 03, Armas e Munição). Hipóteses de playtest, centralizadas aqui.
 */
export const BOW = {
  /** Velocidade da flecha com carga zero; a máxima é o `reach` do arco (m/s). */
  minSpeed: 15,
  gravity: 9.8,
  /** Vida da flecha: 4 s ou 120 m. */
  life: 4,
  range: 120,
  /** Disparo sem carga ainda causa 25 %. */
  minDamage: 0.25,
  /** Carga cheia segurada além de 2 s abre a mira: +1°/s, até 4°. */
  holdSpreadAfter: 2,
  holdSpreadPerSecond: 1,
  holdSpreadMax: 4,
  /** Abertura entre as flechas de um mesmo disparo (Arco do Trovão), em graus. */
  fanDegrees: 2,
  /** Tempo mínimo entre soltar uma flecha e começar a puxar a próxima (s). */
  rearm: 0.25,
  /** Flecha cravada some em 8 s; no Mutirão, metade delas pode ser recolhida. */
  stuckSeconds: 8,
  recoverChance: 0.5,
  /** Do olho até a ponta da flecha ao soltar (m). */
  muzzle: 0.5,
} as const;

export interface AmmoStats {
  damage: number;
  vsHaunt?: number;
}

/** Arco do item (família `arco`), ou `undefined`. */
export function bowOf(itemId: string | undefined): WeaponStats | undefined {
  const weapon = itemId ? itemDef(itemId)?.weapon : undefined;
  return weapon?.family === "arco" ? weapon : undefined;
}

/** Munição do item (vai na Aljava), ou `undefined`. */
export function ammoOf(itemId: string | undefined): AmmoStats | undefined {
  return itemId ? itemDef(itemId)?.ammo : undefined;
}

/** Tempo de carga do arco até o máximo (s): `charge ÷ velocidade de ataque`. */
export const chargeTime = (bow: WeaponStats): number => (bow.charge ?? 0.9) / bow.speed;

/** Carga (0 a 1) depois de segurar `held` segundos. */
export const bowCharge = (bow: WeaponStats, held: number): number => Math.max(0, Math.min(1, held / chargeTime(bow)));

/** Velocidade da flecha (m/s): de 15 até o máximo do arco, pela carga. */
export const arrowSpeed = (bow: WeaponStats, charge: number): number => BOW.minSpeed + (bow.reach - BOW.minSpeed) * charge;

/** Dano base da flecha: munição × multiplicador do arco × (0,25 + 0,75·carga). */
export const arrowDamage = (bow: WeaponStats, ammo: AmmoStats, charge: number): number => ammo.damage * bow.damage * (BOW.minDamage + (1 - BOW.minDamage) * charge);

/** Contra assombrações vale o maior bônus entre arco e flecha (não multiplica). */
export const arrowVsHaunt = (bow: WeaponStats, ammo: AmmoStats): number => Math.max(bow.vsHaunt ?? 1, ammo.vsHaunt ?? 1);

/** Abertura extra da mira por segurar a carga cheia demais (graus). */
export function holdSpread(bow: WeaponStats, held: number): number {
  const over = held - chargeTime(bow) - BOW.holdSpreadAfter;
  return Math.max(0, Math.min(BOW.holdSpreadMax, over * BOW.holdSpreadPerSecond));
}
